import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { type NotificationColorVariant, colorVariants } from '@/theme/tokens';

export type NotificationAlertProps = {
  children: ReactNode;
  type?: NotificationColorVariant;
  dismissible?: boolean;
  onClose?: () => void;
  className?: string;
  position?: 'top' | 'bottom' | 'relative';
  duration?: number;
};

const icons: Record<
  NotificationColorVariant,
  React.FC<{ size?: number; className?: string }>
> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,
};

const iconClasses: Record<NotificationColorVariant, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

const positionClasses = {
  top: 'fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-2xl w-full mx-4',
  bottom:
    'fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] max-w-2xl w-full mx-4',
  relative: '',
};

function NotificationAlert({
  children,
  type = 'info',
  dismissible = false,
  onClose,
  className,
  position = 'relative',
  duration = 0,
}: Readonly<NotificationAlertProps>) {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (duration > 0 && onClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [duration, onClose, handleClose]);

  const getAnimationClasses = () => {
    if (position === 'top') {
      return isClosing
        ? 'opacity-0 -translate-y-full'
        : 'opacity-100 translate-y-0 animate-slideInTop';
    }
    if (position === 'bottom') {
      return isClosing
        ? 'opacity-0 translate-y-full'
        : 'opacity-100 translate-y-0 animate-slideInBottom';
    }
    return isClosing ? 'opacity-0' : 'opacity-100';
  };

  const AlertIcon = icons[type];

  const alertContent = (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 rounded-lg border p-4 transition-all duration-300 ease-out',
        colorVariants.alert[type],
        positionClasses[position],
        getAnimationClasses(),
        className
      )}
    >
      <span className={iconClasses[type]}>
        <AlertIcon size={24} />
      </span>
      <span className="flex-1 text-sm">{children}</span>
      {dismissible && onClose && (
        <button
          onClick={handleClose}
          className="shrink-0 cursor-pointer rounded p-0.5 text-current opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
          aria-label="Close alert"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );

  if (position !== 'relative' && typeof document !== 'undefined') {
    return createPortal(alertContent, document.body);
  }

  return alertContent;
}

export type { NotificationAlertProps as AlertProps };
export { NotificationAlert };
export default NotificationAlert;
